import React from "react";
import { Box, Stack, Typography, Avatar } from "@mui/material";
import {
  UploadFile as UploadIcon,
  SwapHoriz as StatusIcon,
  ChatBubbleOutline as CommentIcon,
  SmartToy as AIIcon,
  Settings as SystemIcon,
} from "@mui/icons-material";
import { tokens } from "@/tokens";

interface ActivityEvent {
  _id: string;
  type: string;
  title: string;
  description: string;
  user: string;
  createdAt: string;
}

const getInitials = (name: string): string => {
  return name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
};

const EVENT_ICONS: Record<string, React.ReactNode> = {
  DOCUMENT_UPLOADED: <UploadIcon sx={{ fontSize: 14, color: tokens.chip.blue.text }} />,
  STATUS_CHANGE: <StatusIcon sx={{ fontSize: 14, color: tokens.chip.green.text }} />,
  COMMENT: <CommentIcon sx={{ fontSize: 14, color: tokens.chip.indigo.text }} />,
  VALIDATION: <AIIcon sx={{ fontSize: 14, color: tokens.chip.amber.text }} />,
  SYSTEM: <SystemIcon sx={{ fontSize: 14, color: tokens.text.muted }} />,
};

const EVENT_COLORS: Record<string, string> = {
  DOCUMENT_UPLOADED: tokens.chip.blue.bg,
  STATUS_CHANGE: tokens.chip.green.bg,
  COMMENT: tokens.chip.indigo.bg,
  VALIDATION: tokens.chip.amber.bg,
  SYSTEM: tokens.chip.gray.bg,
};

const formatTimestamp = (ts: string): string => {
  if (!ts) return "";
  const date = new Date(ts);
  if (isNaN(date.getTime())) return ts;
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

interface ActivityTimelineProps {
  events: ActivityEvent[];
}

const ActivityTimeline: React.FC<ActivityTimelineProps> = ({ events }) => {
  if (events.length === 0) {
    return (
      <Typography sx={{ fontSize: tokens.fontSize.sm2, color: tokens.text.muted, py: 2 }}>
        No activity yet.
      </Typography>
    );
  }

  return (
    <Stack spacing={0}>
      {events.map((event, index) => {
        const isLast = index === events.length - 1;
        return (
          <Stack key={event._id} direction="row" spacing={2}>
            {/* Timeline line + icon */}
            <Box
              sx={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                width: 32,
                flexShrink: 0,
              }}
            >
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  bgcolor: EVENT_COLORS[event.type] || tokens.bg.muted,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                {EVENT_ICONS[event.type]}
              </Box>
              {!isLast && (
                <Box
                  sx={{
                    width: 2,
                    flex: 1,
                    bgcolor: tokens.border.light,
                    my: 0.5,
                  }}
                />
              )}
            </Box>

            {/* Content */}
            <Box sx={{ flex: 1, pb: isLast ? 0 : 2.5 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.25 }}>
                {event.user !== "System" && (
                  <Avatar
                    sx={{
                      width: 20,
                      height: 20,
                      fontSize: tokens.fontSize["2xs"],
                      fontWeight: tokens.fontWeight.bold,
                      bgcolor: tokens.bg.hover,
                      color: tokens.brand.primary,
                    }}
                  >
                    {getInitials(event.user)}
                  </Avatar>
                )}
                <Typography
                  sx={{
                    fontSize: tokens.fontSize.xs,
                    fontWeight: tokens.fontWeight.semibold,
                    color: tokens.text.body,
                  }}
                >
                  {event.user}
                </Typography>
                <Typography sx={{ fontSize: tokens.fontSize["2xs"], color: tokens.text.muted }}>
                  {formatTimestamp(event.createdAt)}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: tokens.fontSize.sm2,
                  fontWeight: tokens.fontWeight.semibold,
                  color: tokens.text.primary,
                  mb: 0.25,
                }}
              >
                {event.title}
              </Typography>
              <Typography sx={{ fontSize: tokens.fontSize.xs, color: tokens.text.secondary }}>
                {event.description}
              </Typography>
            </Box>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ActivityTimeline;
